import type {
  AuditEntry,
  EditionDraftV1,
  EditionSummary,
  ModerationAction,
  ModerationItem,
  ModerationPolicy,
  ModerationReport,
  ModerationReportDecision,
  Page,
  RevisionSummary,
  ValidationResult,
  VersionedDraft,
} from '../types';

const API_ORIGIN = (import.meta.env.VITE_API_ORIGIN ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: unknown;

  constructor(status: number, message: string, code?: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    if (code !== undefined) this.code = code;
    if (details !== undefined) this.details = details;
  }
}

interface ErrorPayload {
  error?: { code?: string; message?: string; details?: unknown } | string;
  message?: string;
}

function unwrap<T>(value: unknown): T {
  if (value && typeof value === 'object' && 'data' in value) {
    return (value as { data: T }).data;
  }
  return value as T;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  headers.set('X-Requested-With', 'lir-edition-studio');
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes((init.method ?? 'GET').toUpperCase())) {
    headers.set('X-LIR-CSRF', '1');
  }

  if (init.body !== undefined && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_ORIGIN}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    let payload: ErrorPayload | undefined;
    try {
      payload = (await response.json()) as ErrorPayload;
    } catch {
      payload = undefined;
    }
    const nested = payload?.error;
    const message =
      typeof nested === 'string'
        ? nested
        : nested?.message ?? payload?.message ?? `Request failed (${response.status})`;
    throw new ApiError(
      response.status,
      message,
      typeof nested === 'object' ? nested.code : undefined,
      typeof nested === 'object' ? nested.details : undefined,
    );
  }

  if (response.status === 204) return undefined as T;
  return unwrap<T>(await response.json());
}

async function requestWithResponse<T>(
  path: string,
  init: RequestInit = {},
): Promise<{ data: T; response: Response }> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  headers.set('X-Requested-With', 'lir-edition-studio');
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes((init.method ?? 'GET').toUpperCase())) {
    headers.set('X-LIR-CSRF', '1');
  }
  if (init.body !== undefined && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_ORIGIN}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });
  if (!response.ok) {
    let payload: ErrorPayload | undefined;
    try {
      payload = (await response.json()) as ErrorPayload;
    } catch {
      payload = undefined;
    }
    const nested = payload?.error;
    throw new ApiError(
      response.status,
      typeof nested === 'string'
        ? nested
        : nested?.message ?? payload?.message ?? `Request failed (${response.status})`,
      typeof nested === 'object' ? nested.code : undefined,
      typeof nested === 'object' ? nested.details : undefined,
    );
  }
  return { data: unwrap<T>(await response.json()), response };
}

function json(value: unknown): string {
  return JSON.stringify(value);
}

export const adminApi = {
  listEditions: () => request<EditionSummary[]>('/api/v2/admin/editions'),

  createEdition: (input: {
    title: string;
    type: 'summer' | 'winter';
    startsAt: string;
    endsAt: string;
    timezone: string;
  }) =>
    request<EditionSummary>('/api/v2/admin/editions', {
      method: 'POST',
      body: json(input),
    }),

  async getDraft(editionId: string): Promise<VersionedDraft> {
    const { data, response } = await requestWithResponse<EditionDraftV1>(
      `/api/v2/admin/editions/${encodeURIComponent(editionId)}/draft`,
    );
    return {
      draft: data,
      etag: response.headers.get('ETag') ?? response.headers.get('X-Draft-Version') ?? '"0"',
    };
  },

  async saveDraft(
    editionId: string,
    draft: EditionDraftV1,
    etag: string,
  ): Promise<VersionedDraft> {
    const { data, response } = await requestWithResponse<EditionDraftV1>(
      `/api/v2/admin/editions/${encodeURIComponent(editionId)}/draft`,
      {
        method: 'PUT',
        headers: { 'If-Match': etag },
        body: json(draft),
      },
    );
    return {
      draft: data,
      etag: response.headers.get('ETag') ?? response.headers.get('X-Draft-Version') ?? etag,
    };
  },

  validateDraft: (editionId: string, draft: EditionDraftV1) =>
    request<ValidationResult>(
      `/api/v2/admin/editions/${encodeURIComponent(editionId)}/draft/validate`,
      { method: 'POST', body: json(draft) },
    ),

  publishDraft: (editionId: string, expectedEtag: string) =>
    request<RevisionSummary>(
      `/api/v2/admin/editions/${encodeURIComponent(editionId)}/publish`,
      {
        method: 'POST',
        headers: { 'If-Match': expectedEtag },
        body: json({ expectedEtag }),
      },
    ),

  listRevisions: (editionId: string) =>
    request<RevisionSummary[]>(
      `/api/v2/admin/editions/${encodeURIComponent(editionId)}/revisions`,
    ),

  restoreRevision: (editionId: string, releaseId: string, expectedEtag: string) =>
    request<VersionedDraft>(
      `/api/v2/admin/editions/${encodeURIComponent(editionId)}/revisions/${encodeURIComponent(releaseId)}/restore`,
      { method: 'POST', headers: { 'If-Match': expectedEtag }, body: json({ releaseId }) },
    ),

  async importTimetable(
    editionId: string,
    source: string,
    format: 'csv' | 'json',
    expectedEtag: string,
  ): Promise<VersionedDraft> {
    const { data, response } = await requestWithResponse<EditionDraftV1>(
      `/api/v2/admin/editions/${encodeURIComponent(editionId)}/timetable/import`,
      { method: 'POST', headers: { 'If-Match': expectedEtag }, body: json({ format, source }) },
    );
    return {
      draft: data,
      etag: response.headers.get('ETag') ?? response.headers.get('X-Draft-Version') ?? '"0"',
    };
  },

  uploadAsset: (editionId: string, file: File, purpose: 'map-overlay' | 'chat-test') => {
    const body = new FormData();
    body.set('file', file);
    body.set('purpose', purpose);
    return request<{ assetKey: string; url: string }>(
      `/api/v2/admin/editions/${encodeURIComponent(editionId)}/assets/upload`,
      { method: 'POST', body },
    );
  },

  getModerationPolicy: () =>
    request<ModerationPolicy>('/api/v2/admin/moderation/policy'),

  saveModerationPolicy: (policy: ModerationPolicy) =>
    request<ModerationPolicy>('/api/v2/admin/moderation/policy', {
      method: 'PUT',
      headers: { 'If-Match': `"${policy.version}"` },
      body: json(policy),
    }),

  listModerationItems: (status: string, cursor?: string) => {
    const params = new URLSearchParams({ status });
    if (cursor !== undefined) params.set('cursor', cursor);
    return request<Page<ModerationItem>>(`/api/v2/admin/moderation/items?${params}`);
  },

  moderate: (itemId: string, action: ModerationAction, reason: string) =>
    request<{ id: string; status: 'visible_pending' | 'allowed' | 'removed' }>(
      `/api/v2/admin/moderation/items/${encodeURIComponent(itemId)}/actions`,
      { method: 'POST', body: json({ action, reason }) },
    ),

  rescanModeration: () =>
    request<{ accepted: boolean; queued: number }>('/api/v2/admin/moderation/rescan', {
      method: 'POST',
      body: json({ windowMinutes: 60 }),
    }),

  listModerationReports: (status: 'open' | 'resolved' | 'dismissed', cursor?: string) => {
    const params = new URLSearchParams({ status });
    if (cursor !== undefined) params.set('cursor', cursor);
    return request<Page<ModerationReport>>(`/api/v2/admin/moderation/reports?${params}`);
  },

  decideModerationReport: (
    reportId: string,
    decision: ModerationReportDecision,
    note: string,
  ) =>
    request<{ id: string; status: ModerationReportDecision; resolvedAt: string }>(
      `/api/v2/admin/moderation/reports/${encodeURIComponent(reportId)}/decision`,
      { method: 'POST', body: json({ decision, note }) },
    ),

  listAudit: (cursor?: string) => {
    const params = new URLSearchParams();
    if (cursor !== undefined) params.set('cursor', cursor);
    const query = params.size > 0 ? `?${params}` : '';
    return request<Page<AuditEntry>>(`/api/v2/admin/audit${query}`);
  },
};
